import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { ImagesEntity } from 'libs/gb-dashboard/shared/data-access-images/src'
import {
  ImageWidget,
  ImageWidgetSourceEnum,
} from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { GbPresenterWidgetPlaceholderComponent } from '../gb-presenter-widget-placeholder/gb-presenter-widget-placeholder.component'
import { TranslateModule } from '@ngx-translate/core'
import { ImageSizeEnum } from 'libs/shared/models/images/image.models'
import { GbImageComponent } from 'libs/shared/images/ui/src/lib/gb-image/gb-image.component'

@Component({
  selector: 'gb-presenter-widget-image',
  imports: [GbPresenterWidgetPlaceholderComponent, TranslateModule, GbImageComponent],
  templateUrl: './gb-presenter-widget-image.component.html',
  styleUrl: './gb-presenter-widget-image.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterWidgetImageComponent {
  private readonly selectedAuctionFacade = inject(SelectedAuctionFacade)

  private readonly selectedAuction = toSignal(this.selectedAuctionFacade.auction$, {
    initialValue: null,
  })

  ImageSizeEnum = ImageSizeEnum

  @Input({ required: true }) imageWidget!: ImageWidget

  private get widgetSource(): ImageWidgetSourceEnum {
    return this.imageWidget?.image_source ?? ImageWidgetSourceEnum.Custom
  }

  get showPlaceholder(): boolean {
    if (this.widgetSource === ImageWidgetSourceEnum.AuctionBanner) {
      return !this.selectedBannerImage
    }
    if (this.widgetSource === ImageWidgetSourceEnum.AuctionLogo) {
      return !this.selectedAuctionLogo
    }
    return false
  }

  get placeholderTitleTranslateKey(): string {
    if (this.widgetSource === ImageWidgetSourceEnum.AuctionBanner) {
      return 'PRESENTER.IMAGE_WIDGET_PLACEHOLDER.NO_BANNER_TITLE'
    }
    return 'PRESENTER.IMAGE_WIDGET_PLACEHOLDER.NO_LOGO_TITLE'
  }

  get placeholderBodyTranslateKey(): string {
    if (this.widgetSource === ImageWidgetSourceEnum.AuctionBanner) {
      return 'PRESENTER.IMAGE_WIDGET_PLACEHOLDER.NO_BANNER_BODY'
    }
    return 'PRESENTER.IMAGE_WIDGET_PLACEHOLDER.NO_LOGO_BODY'
  }

  private get selectedAuctionLogo(): ImagesEntity | null {
    const logo = this.selectedAuction()?.logo
    if (logo?.image) {
      return logo
    }
    return null
  }

  private get selectedBannerImage(): ImagesEntity | null {
    const banners = this.selectedAuction()?.banners || []
    const banner = banners
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .at(0)?.image
    if (banner?.image) {
      return banner
    }
    return null
  }

  get imageEntity(): ImagesEntity {
    if (this.widgetSource === ImageWidgetSourceEnum.AuctionLogo) {
      return this.selectedAuctionLogo || this.getFallbackEntity('')
    }

    if (this.widgetSource === ImageWidgetSourceEnum.AuctionBanner) {
      return this.selectedBannerImage || this.getFallbackEntity('')
    }

    const url = this.imageWidget?.image?.image || ''
    return this.getFallbackEntity(url)
  }

  private getFallbackEntity(url: string): ImagesEntity {
    return {
      auction: '',
      uuid: this.imageWidget?.uuid || '',
      image: url,
      medium_image: url,
      preload_image: url,
      small_image: url,
      thumbnail_image: url,
    }
  }

  get cover(): boolean {
    return true
  }
}
